import { Box, VStack, Grid, Separator } from "@chakra-ui/react";
import SectionHeader from "../../../CustomUi/SectionHeader";
import TermConfigSection from "./TermConfigSection";
import { BiPlus } from "react-icons/bi";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import { Term } from "../../../../services/Dtos";
import { useEffect, useState } from "react";
import Loader from "../../../CustomUi/Loader";
import CustomAlert from "../../../CustomUi/CustomAlert";
import { getNextOrder, formatDate } from "../../../../services/Utils";
import TermModal from "./TermModal";
import TermCard from "./TermCard";
import Empty from "../../../CustomUi/Empty";
import useIcons from "../../../../hooks/useIcons";
import Cad from "../../../CustomUi/Cad";
import { useDisclosure } from "@chakra-ui/react";
import { useFormFieldHandler } from "../../../../hooks/useFormFieldHandler";
import { useSchoolService } from "../../../../hooks/Domain/useSchoolService";

function TermSettings() {
  const { school, terms, isLoading, error, accentColor } = useSchoolsContext();

  const [termToEdit, setTermToEdit] = useState<Term | null>(null);
  const { onOpen, open, onClose } = useDisclosure();

  const {
    updateCurrentTerm,
    updateCurrentTermDaysOpen,
    updateNextTerm,
    updateNextTermStartDate,
    updateCurrentTermStartDate,
    updateCurrentTermEndDate,
    updateLeapTerm,
    currentTermLoading,
    currentTermDaysOpenLoading,
    nextTermLoading,
    nextTermStartDateLoading,
    currentTermStartDateLoading,
    currentTermEndDateLoading,
    leapTermLoading,
  } = useSchoolService();

  const [termConfig, setTermConfig] = useState({
    currentTermId: school?.currentTermId || "",
    nextTermId: school?.nextTermId || "",
    currentTermDaysOpen: school?.currentTermDaysOpen || 0,
    nextTermStartDate: school?.nextTermStartDate || "",
    currentTermStartDate: school?.currentTermStartDate || "",
    currentTermEndDate: school?.currentTermEndDate || "",
    leapTermId: school?.leapTermId || "",
  });

  const [_, setTouchedFields] = useState<Set<string>>(new Set());

  const { handleDirectFieldChange } = useFormFieldHandler({
    setFormState: setTermConfig,
    setTouchedFields,
    integerFields: ["currentTermDaysOpen"],
  });

  const [nextOrder, setNextOrder] = useState(0);

  const icons = useIcons();

  useEffect(() => {
    if (school) {
      setTermConfig({
        currentTermId: school.currentTermId || "",
        nextTermId: school.nextTermId || "",
        currentTermDaysOpen: school.currentTermDaysOpen || 0,
        nextTermStartDate: school.nextTermStartDate
          ? formatDate(school.nextTermStartDate)
          : "",
        currentTermStartDate: school.currentTermStartDate
          ? formatDate(school.currentTermStartDate)
          : "",
        currentTermEndDate: school.currentTermEndDate
          ? formatDate(school.currentTermEndDate)
          : "",
        leapTermId: school.leapTermId || "",
      });
    }
  }, [school, terms]);

  const handleRefresh = () => {
    // Refresh functionality can be implemented here
  };

  const handleAddTerm = () => {
    setTermToEdit(null);
    setNextOrder(getNextOrder(terms));
    onOpen();
  };

  const handleEditTerm = (term: Term) => {
    setTermToEdit(term);
    onOpen();
  };

  const handleCurrentTermSave = async () => {
    if (!termConfig.currentTermId) {
      return;
    }
    await updateCurrentTerm(termConfig.currentTermId);
  };

  const handleCurrentTermDaysOpenSave = async () => {
    if (!termConfig.currentTermDaysOpen) {
      return;
    }
    await updateCurrentTermDaysOpen(termConfig.currentTermDaysOpen);
  };

  const handleNextTermSave = async () => {
    if (!termConfig.nextTermId) {
      return;
    }
    await updateNextTerm(termConfig.nextTermId);
  };

  const handleNextTermStartDateSave = async () => {
    if (!termConfig.nextTermStartDate) {
      return;
    }
    await updateNextTermStartDate(termConfig.nextTermStartDate);
  };

  const handleCurrentTermStartDateSave = async () => {
    if (!termConfig.currentTermStartDate) {
      return;
    }
    await updateCurrentTermStartDate(termConfig.currentTermStartDate);
  };

  const handleCurrentTermEndDateSave = async () => {
    if (!termConfig.currentTermEndDate) {
      return;
    }
    await updateCurrentTermEndDate(termConfig.currentTermEndDate);
  };

  const handleLeapTermSave = async () => {
    if (!termConfig.leapTermId) {
      return;
    }
    await updateLeapTerm(termConfig.leapTermId);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <CustomAlert message={error} handleClick={handleRefresh} />;
  }

  const termOptions = terms.map((t) => ({
    id: t.id,
    name: t.termName,
  }));

  return (
    <VStack gap={8}>
      <TermModal
        isOpen={open}
        onClose={onClose}
        termToEdit={termToEdit}
        nextOrder={nextOrder}
      />

      {/* Terms Management Section */}
      <Cad>
        <VStack gap={6} align="stretch">
          <SectionHeader
            title="Academic Terms"
            subtitle="Create and manage your school's academic terms"
            icon={icons.main.term}
            buttonText="Add Term"
            onButtonClick={handleAddTerm}
            buttonIcon={BiPlus}
            buttonColorScheme={accentColor}
          />

          <Separator />

          {/* Terms Grid */}
          {terms.length > 0 ? (
            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                xl: "repeat(3, 1fr)",
              }}
              gap={6}
            >
              {terms.map((t) => (
                <TermCard
                  key={t.id}
                  term={t}
                  handleClick={() => handleEditTerm(t)}
                />
              ))}
            </Grid>
          ) : (
            <Box py={8}>
              <Empty
                message="No terms created yet"
                hasButton
                buttonText="Create First Term"
                handleClick={handleAddTerm}
              />
            </Box>
          )}
        </VStack>
      </Cad>

      {/* Term Configuration Sections */}
      {terms.length > 0 && (
        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
          }}
          gap={6}
          w={"full"}
        >
          {/* Current Term Selection */}
          <TermConfigSection
            title="Current Term"
            subtitle="Set the active term for result calculations"
            description="Select the current term for the school. This term will be used for all result computations."
            inputType="select"
            value={termConfig.currentTermId}
            onChange={(e) =>
              handleDirectFieldChange("currentTermId", e.target.value)
            }
            onSave={handleCurrentTermSave}
            isLoading={currentTermLoading}
            isDisabled={!termConfig.currentTermId}
            buttonText="Save Current Term"
            options={termOptions}
            accentColor={accentColor}
          />

          {/* Current Term Days Open */}
          <TermConfigSection
            title="Current Term Days Open"
            subtitle="Set the number of days the current term is open"
            description="This setting determines how many days the current term is open for result entry."
            inputType="number"
            value={termConfig.currentTermDaysOpen}
            onChange={(e) =>
              handleDirectFieldChange("currentTermDaysOpen", e.target.value)
            }
            onSave={handleCurrentTermDaysOpenSave}
            isLoading={currentTermDaysOpenLoading}
            isDisabled={!termConfig.currentTermDaysOpen}
            buttonText="Save Days Open"
            placeholder="Enter number of days"
            accentColor={accentColor}
          />

          {/* Next Term Selection */}
          <TermConfigSection
            title="Next Term"
            subtitle="Set the next term for planning"
            description="Select the next term for planning purposes and result preparation."
            inputType="select"
            value={termConfig.nextTermId}
            onChange={(e) =>
              handleDirectFieldChange("nextTermId", e.target.value)
            }
            onSave={handleNextTermSave}
            isLoading={nextTermLoading}
            isDisabled={!termConfig.nextTermId}
            buttonText="Save Next Term"
            options={termOptions}
            accentColor={accentColor}
          />

          {/* Next Term Start Date */}
          <TermConfigSection
            title="Next Term Start Date"
            subtitle="Set the start date for the next term"
            description="This date will be used for planning and scheduling purposes."
            inputType="date"
            value={termConfig.nextTermStartDate}
            onChange={(e) =>
              handleDirectFieldChange("nextTermStartDate", e.target.value)
            }
            onSave={handleNextTermStartDateSave}
            isLoading={nextTermStartDateLoading}
            isDisabled={!termConfig.nextTermStartDate}
            buttonText="Save Start Date"
            accentColor={accentColor}
          />

          {/* Current Term Start Date */}
          <TermConfigSection
            title="Current Term Start Date"
            subtitle="Set the start date for the current term"
            description="This date is used for term duration calculations and reporting."
            inputType="date"
            value={termConfig.currentTermStartDate}
            onChange={(e) =>
              handleDirectFieldChange("currentTermStartDate", e.target.value)
            }
            onSave={handleCurrentTermStartDateSave}
            isLoading={currentTermStartDateLoading}
            isDisabled={!termConfig.currentTermStartDate}
            buttonText="Save Start Date"
            accentColor={accentColor}
          />

          {/* Current Term End Date */}
          <TermConfigSection
            title="Current Term End Date"
            subtitle="Set the end date for the current term"
            description="This date is used for term duration calculations and reporting."
            inputType="date"
            value={termConfig.currentTermEndDate}
            onChange={(e) =>
              handleDirectFieldChange("currentTermEndDate", e.target.value)
            }
            onSave={handleCurrentTermEndDateSave}
            isLoading={currentTermEndDateLoading}
            isDisabled={!termConfig.currentTermEndDate}
            buttonText="Save End Date"
            accentColor={accentColor}
          />

          {/* Leap Term Selection */}
          <TermConfigSection
            title="Leap Term"
            subtitle="Set the leap term for special cases"
            description="A leap term is used for special academic periods or remedial sessions."
            inputType="select"
            value={termConfig.leapTermId}
            onChange={(e) =>
              handleDirectFieldChange("leapTermId", e.target.value)
            }
            onSave={handleLeapTermSave}
            isLoading={leapTermLoading}
            isDisabled={!termConfig.leapTermId}
            buttonText="Save Leap Term"
            options={termOptions}
            accentColor={accentColor}
          />
        </Grid>
      )}
    </VStack>
  );
}

export default TermSettings;
